export interface CoverageTooltipData {
  values: string[];
  labels: string[];
  colors: string[];
  formulas?: string[]; // Optional detailed formulas for each value
  subtitles?: string[]; // Optional subtitles under labels
  footer?: string; // Optional footer content
  separators?: number[]; // Row indices where a separator line appears BEFORE that row
  html?: string; // When set, bypasses label/value rendering and uses this HTML directly
  rawValues?: { [key: string]: number }; // Raw values for calculations
}

export const renderCoverageTooltipHTML = ({ values, labels, colors, formulas, subtitles, footer, separators, html, rawValues }: CoverageTooltipData): string => {
  // If a renderer supplied pre-built HTML, use it directly
  if (html) return `<div class="relative">${html}</div>`;

  const getRowHTML = (label: string, value: string, color: string, formula?: string, subtitle?: string, isOnHand?: boolean) => {
    // Use green color for on-hand storage
    const displayColor = isOnHand ? '#10B981' : (color || 'gray');
    
    return `
     <div class="mb-2">
        <div class="flex items-center gap-2 mb-1">
          <div class="w-3 h-3 rounded-sm" style="background-color: ${displayColor};"></div>
          <div>
            <span class="text-sm text-gray-700 font-medium">${label || 'Expected Demand'}</span>
            ${subtitle ? `<div class="text-xs text-gray-500">${subtitle}</div>` : ''}
          </div>
        </div>
        <div class="ml-5">
          <div class="text-base font-semibold text-gray-900">${value}</div>
          ${formula ? `<div class="text-xs text-gray-500 mt-0.5">${formula}</div>` : ''}
        </div>
      </div>
    `;
  };

  const separatorHTML = '<div class="my-2 border-t border-gray-200"></div>';
  const separatorSet = new Set(separators || []);
  
  // Check if this is on-hand tooltip (only one row with "On-Hand Storage" label)
  const isOnHand = labels.length > 0 && labels[0] === 'On-Hand Storage';
  
  // Build rows, inserting separators where specified
  const rows = labels
    .map((label, index) => ({
      label,
      value: values[index],
      color: colors[index] || '#6B7280',
      formula: formulas?.[index],
      subtitle: subtitles?.[index],
      index
    }))
    .filter(row => row.label && row.value) // Only include rows with both label and value
    .map(row => {
      const sep = separatorSet.has(row.index) ? separatorHTML : '';
      return sep + getRowHTML(row.label, row.value, row.color, row.formula, row.subtitle, isOnHand && row.index === 0);
    })
    .join('');
  
  return `
    <div class="relative">
      ${rows}
      ${footer ? `<div class="mt-3 pt-3 border-t border-gray-200">${footer}</div>` : ''}
    </div>
  `;
};
